import { InputNumberProps } from 'antd';

import { formatCurrency, formatNumber } from 'utils/formatters';

import useFeatureFlags from './useFeatureFlags';

/**
 * The hook exists to help implementing decimal loyalty points on the platform.
 * All decimal points are stored as `x * 100` integers in db.
 *
 * @returns `formatPoints`: function for points formatting
 * @returns `pointsInputProps`: props which should be passed to any points number input
 */
export const usePointsFormat = () => {
  const { loyalty_points_decimals } = useFeatureFlags();

  const formatPoints = (value: number) =>
    loyalty_points_decimals ? formatCurrency(value / 100) : formatNumber(value);

  const pointsInputProps: Partial<InputNumberProps<number>> = loyalty_points_decimals
    ? {
        // PLA-9531: an emptied field must render EMPTY. `String(undefined / 100)` produced the
        // literal string "NaN" in the input, and null/'' produced "0" — which is why the reported
        // repro only arms once the field has been cleared.
        formatter: value =>
          value === undefined || value === null || String(value) === ''
            ? ''
            : String(Number(value) / 100),
        // PLA-9531: `parseFloat(s) * 100` is binary-lossy — 1.15 becomes 114.99999999999999 and
        // 0.29 becomes 28.999999999999996, silently shaving a cent off values the user is entitled
        // to enter. Round: the display is capped at 2 places, so this is exact, never a fudge.
        parser: value => {
          if (value === undefined || value === null || value === '') {
            return value as unknown as number;
          }
          const points = parseFloat(value);
          return Number.isNaN(points) ? (value as unknown as number) : Math.round(points * 100);
        },
        step: 0.01
      }
    : {
        step: 1
      };

  return { formatPoints, pointsInputProps };
};

export default usePointsFormat;
